import { useRouter } from "next/router";
import qs from "qs";
import type { ParsedQs } from "qs";
import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { BaseLink } from "shared/components/common/base-link";

import { QueryParameter } from "./consts";
import { getLetterFromQueryString, letters } from "./helpers";
import { Container, Letter } from "./styles";

export type Props = {
  route(
    query: QueryParameters<{
      [QueryParameter]?: string;
    }>,
  ): {
    pathname: string;
    query: { pageType: string };
  };
};

export const LetterList: FunctionComponent<Props> = ({ route, ...attributes }) => {
  const { query, replace } = useRouter();
  const selectedLetter = getLetterFromQueryString(query[QueryParameter]);

  const computeSelectedLetterRoutePath = ({
    letter,
    parsedQuery = {},
    pathname,
  }: {
    letter: string;
    parsedQuery?: ParsedQs;
    pathname: string;
  }) => `${pathname}?${qs.stringify({ [QueryParameter]: letter, ...parsedQuery })}`;

  // Replace the route if char is not defined
  useEffect(() => {
    if (query[QueryParameter]) return;
    const pageRoute = route({ [QueryParameter]: query[QueryParameter] });
    const parsedQuery = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    void replace(
      computeSelectedLetterRoutePath({
        letter: selectedLetter,
        parsedQuery,
        pathname: pageRoute.pathname,
      }),
    );
  }, [query, replace, route, selectedLetter]);

  return (
    <Container {...attributes}>
      {letters.map((letter, index) => {
        const pageRoute = route({ [QueryParameter]: letter });
        const isActiveLetter = letter === selectedLetter;
        const renderLetter = (
          <Letter $isActive={isActiveLetter} key={index}>
            {letter}
          </Letter>
        );
        if (isActiveLetter) return renderLetter;
        return (
          <BaseLink
            key={index}
            {...pageRoute}
            as={computeSelectedLetterRoutePath({ letter, pathname: pageRoute.pathname })}
          >
            {renderLetter}
          </BaseLink>
        );
      })}
    </Container>
  );
};

LetterList.displayName = "LetterList";
